import React from 'react';
import styled from '@emotion/styled';
import CountUp from 'react-countup';
import { rem } from 'polished';
import { colors, mixins, spaces } from '../../core/styles/theme/default';
import { ImageInterface } from '../../types/ImageInterface';
import ImageWithCaption from '../Image/ImageWithCaption';
import { P2 } from '../../primitives';

const StyledCounter = styled.div`
  display: grid;
  justify-content: center;
  gap: ${rem(spaces.delta)};

  ${mixins.responsiveStyles({
    'grid-template-columns': {
      zero: '1fr',
      small: `repeat(2, 1fr)`,
      medium: `repeat(3, 1fr)`,
      large: `repeat(4, 1fr)`,
    },
  })}
`;

const StyledItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${rem(spaces.alpha)};
  padding: ${rem(spaces.gamma)};
  border-radius: ${rem(2)};
  border: ${rem(1)} solid ${colors.primary};
  background: ${colors.white};
  color: ${colors.primary};

  ${({ theme }) => theme?.stylesExtension?.counter?.item}
`;

const StyledLabel = styled(P2)`
  line-height: 1.5;
`;

const StyledNumberWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${rem(spaces.alpha)};
`;

const StyledIcon = styled.div`
  img {
    object-fit: contain !important;

    ${mixins.responsiveStyles({
      width: {
        zero: 48,
        large: 64,
      },
      height: {
        zero: 48,
        large: 64,
      },
    })}
  }
`;

const StyledNumber = styled(CountUp)`
  margin-left: auto;
  font-weight: 700;
  line-height: 1.2;
  word-break: break-all;

  ${mixins.responsiveStyles({
    'font-size': {
      zero: 24,
      large: 32,
    },
  })}
`;

const Counter = ({ items }: CounterProps) => (
  <StyledCounter>
    {items.map(({ label, value, icon, suffix }) => (
      <StyledItem key={label}>
        <StyledLabel>{label}</StyledLabel>
        <StyledNumberWrapper>
          {icon && (
            <StyledIcon>
              <ImageWithCaption data={{ ...icon, imageFill: false }} />
            </StyledIcon>
          )}
          <StyledNumber
            enableScrollSpy
            scrollSpyOnce
            separator={"'"}
            end={value}
            suffix={suffix}
          />
        </StyledNumberWrapper>
      </StyledItem>
    ))}
  </StyledCounter>
);

export type CounterProps = {
  items: {
    label: string;
    value: number;
    icon?: ImageInterface;
    suffix?: string;
  }[];
};

export default Counter;
